import { useI18N } from "@customer-tools-web/component-i18n";
import { mediaMax, theme, Icon } from "@customer-tools-web/component-ui";
import * as dayjs from "dayjs";
import { History } from "history";
import * as React from "react";
import { useHistory } from "react-router";
import styled from "styled-components";

import { NotificationType } from "../Header";

interface NotificationProps {
  braze: any;
  onClose?: () => void;
}

const Notification: React.FC<NotificationProps> = (
  props: NotificationProps
): React.ReactElement<void> => {
  const [data, setData] = React.useState([]);
  const [lastUpdated, setLastUpdated] = React.useState("");
  const [firstLoad, setFirstLoad] = React.useState(true);
  const history = useHistory();
  const { t } = useI18N();

  const NotificationOnClick = (
    content: NotificationType,
    props: NotificationProps,
    history: History<unknown>
  ) => {
    if (!content.clicked) {
      props.braze.markContentCardAsRead(content);
    }
    if (content.url && content.url.length > 0 && content.url !== "#") {
      if (props.onClose) {
        props.onClose();
      }
      if (
        content.url.startsWith("http://") ||
        content.url.startsWith("https://")
      ) {
        window.open(content.url, "_blank");
      } else {
        history.push(content.url);
      }
    }
  };

  const getLinkTarget = (url: string): string => {
    return url.startsWith("http://") || url.startsWith("https://")
      ? "_blank"
      : "_self";
  };

  function refreshContent() {
    const contents = props.braze.getCachedContentCards();
    setData(contents.cards);
    setLastUpdated(String(contents.lastUpdate));
  }

  React.useEffect(() => {
    refreshContent();

    function storageListener() {
      const ccLastUpdated = props.braze.getContentLastUpdated();
      if (ccLastUpdated !== lastUpdated) {
        refreshContent();
      }
    }
    function ccListener() {
      refreshContent();
    }

    window.addEventListener("storage", storageListener); // cater for different tab event
    window.addEventListener("refreshContentCardContent", ccListener); // cater for same page event

    return function cleanupListener() {
      window.removeEventListener("storage", storageListener);
      window.removeEventListener("refreshContentCardContent", ccListener);
    };
  }, []);

  React.useEffect(() => {
    if (firstLoad) {
      if (data?.length > 0) {
        props.braze.logContentCardsDisplayed(data);
        setFirstLoad(false);
      }
    } else {
      const unViewed = data.filter((x: any) => !x.viewed);
      props.braze.logContentCardsDisplayed(unViewed);
    }
  }, [data]);

  return (
    <span>
      {data?.length ? (
        data.map((content: NotificationType) => (
          <NotificationWrapper
            data-test="notification-item"
            key={content.id}
            hasLink={(content.linkText && content.linkText.length > 0) || false}
            onClick={() => NotificationOnClick(content, props, history)}
          >
            <NotificationMessage>
              <NotificationTitle>{content.title}</NotificationTitle>
              <NotificationDescription
                dangerouslySetInnerHTML={{
                  __html: content.description?.replace("\n", "<br/>") || "",
                }}
              />
              {content.linkText && content.url && (
                <a target={getLinkTarget(content.url)} href={content.url}>
                  {content.linkText}
                </a>
              )}
            </NotificationMessage>
            <NotificationDate>
              {dayjs(content.updated).format("D MMM YYYY")}
            </NotificationDate>
            <NotificationImageWrapper>
              <NotificationImageWrapper2>
                {content.imageUrl ? (
                  <NotificationImage src={content.imageUrl} />
                ) : (
                  <NoNotificationIcon
                    type="bell-round"
                    color={theme.primary}
                    size={"2.2em"}
                  />
                )}
              </NotificationImageWrapper2>
            </NotificationImageWrapper>
            {!content.clicked && (
              <NotificationUnRead data-test="notification-unread" />
            )}
          </NotificationWrapper>
        ))
      ) : (
        <EmptyNotificationWrapper data-test="notification-empty">
          <EmptyNotificationContent>
            <EmptyNotificationIcon
              type="bell-round"
              color={theme.primary}
              size={"51px"}
            />
            <EmptyNotificationSubject>
              {t("notification.emptySubject")}
            </EmptyNotificationSubject>
            <EmptyNotificationMessage>
              {t("notification.emptyMessage")}
            </EmptyNotificationMessage>
          </EmptyNotificationContent>
        </EmptyNotificationWrapper>
      )}
    </span>
  );
};

const NotificationWrapper = styled.div<{ hasLink: boolean }>`
  width: 468px;
  ${mediaMax.sm`width: 100%; box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.2);`};
  min-height: ${({ hasLink }) => (hasLink ? 107 : 85)}px;
  ${mediaMax.sm`min-height: 101px;margin-top:8px;`};
  position: relative;
  &:hover {
    background-color: ${(props) => props.theme.pageBg};
    cursor: pointer;
  }
`;

const NotificationDate = styled.span`
  width: 81px;
  left: 369px;
  top: 11px;
  position: absolute;
  ${mediaMax.sm`left: 278px;top:-33px;position:absolute;width:81px;display:inline-block;`};
  font-size: ${(props) => props?.theme?.fontSize?.small};
  line-height: 16px;
  text-align: right;
`;

const NotificationMessage = styled.div`
  width: 358.18px;
  ${mediaMax.sm`width: 287px;`};
  left: 75px;
  padding-top: 9px;
  padding-bottom: 9px;
  position: relative;
  a {
    color: ${(props) => props?.theme?.blue};
  }
`;

const NotificationTitle = styled.div`
  width: 85%;
  ${mediaMax.sm`width: 100%;`};
  font-size: ${(props) => props?.theme?.fontSize?.body};
  font-weight: 600;
  line-height: 24px;
`;

const NotificationDescription = styled.div`
  width: 85%;
  ${mediaMax.sm`width: 100%;`};
  font-size: ${(props) => props?.theme?.fontSize?.caption};
  font-weight: 400;
  line-height: 20px;
`;

const NotificationImageWrapper = styled.div`
  width: 50px;
  min-height: 107px;
  ${mediaMax.sm`min-height: auto;`};
  left: 17px;
  top: 9px;
  position: absolute;
`;

const NotificationImageWrapper2 = styled.div`
  width: 50px;
  height: 50px;
  left: 0px;
  top: 0px;
  position: absolute;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const NotificationImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 8px;
`;

const NotificationUnRead = styled.div`
  width: 12px;
  height: 12px;
  left: 437px;
  top: 41px;
  ${mediaMax.sm`left:350px;top:15px;`};
  position: absolute;
  background-color: ${(props) => props?.theme?.qualityBlue};
  border-radius: 9999px;
`;

const NoNotificationIcon = styled(Icon)`
  background-color: ${(props) => props?.theme?.primary}1A;
  border-radius: 8px;
  width: 50px;
  height: 50px;
  text-align: center;
`;

const EmptyNotificationIcon = styled(Icon)`
  background-color: ${(props) => props?.theme?.primary}1A;
  border-radius: 9999px;
  width: 123px;
  height: 123px;
  padding: 25px;
  text-align: center;
`;

const EmptyNotificationWrapper = styled.div`
  width: 468px;
  ${mediaMax.sm`width: 375px;`}
  height: 345px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
`;

const EmptyNotificationContent = styled.div`
  position: absolute;
  width: 226px;
  height: 225px;
  left: 122px;
  ${mediaMax.sm`left: 74px;`}
  top: 108px;
  ${mediaMax.sm`top: 74px;`}
  align-items: center;
  justify-content: center;
  text-align: center;
  display: flex;
  flex-direction: column;
`;

const EmptyNotificationSubject = styled.p`
  font-size: ${(props) => props?.theme?.fontSize?.title3};
  line-height: 22px;
  text-align: center;
  color: ${(props) => props?.theme?.defaultIconColor};
  font-weight: 600;
  margin-top: 16px;
`;

const EmptyNotificationMessage = styled.p`
  font-size: ${(props) => props?.theme?.fontSize?.body};
  line-height: 24px;
  text-align: center;
  color: ${(props) => props?.theme?.defaultIconColor};
  height: 16px;
`;

export default Notification;
